import { Box, Typography } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { clamp } from "../../shared/chartMath";
import { percentage } from "../../shared/percentage";
import { TABULAR_NUMERALS } from "./dataAtoms/shared";
import { SegmentedControl } from "./SegmentedControl";

/** How far the bar is lightened from the primary colour. */
const BAR_LIGHTEN = 0.2;

/**
 * The figure stands on the bar from this progress on; a shorter bar has no
 * room for it, and the figure stands beside it.
 */
const FIGURE_ON_BAR_FROM = 0.1;

type Shown = "primary" | "secondary";

/**
 * A progress as one bar with its percentage on it, and a choice between two
 * ways the progress is worked out.
 */
export const ToggleProgressBar = ({
  label,
  primaryLabel,
  secondaryLabel,
  primaryValue,
  secondaryValue,
  barColor,
  barInk,
  trackColor,
  choiceLabel,
}: {
  label: string;
  primaryLabel: string;
  secondaryLabel: string;
  primaryValue?: number;
  secondaryValue?: number;
  barColor?: string;
  /** The colour of the figure on the bar; one that contrasts where left out. */
  barInk?: string;
  trackColor?: string;
  /** What the choice between the two values is, for assistive technology. */
  choiceLabel?: string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "toggleProgressBar" });
  const [shown, setShown] = useState<Shown>("primary");

  if (primaryValue === undefined || secondaryValue === undefined) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("noData")}
      </Typography>
    );
  }

  const progress = shown === "primary" ? primaryValue : secondaryValue;
  const fill = barColor ?? lighten(theme.palette.primary.main, BAR_LIGHTEN);
  const isFigureOnBar = progress >= FIGURE_ON_BAR_FROM;
  const figure = (
    <Box
      component="span"
      sx={{ typography: "body2", fontWeight: 700, ...TABULAR_NUMERALS }}
    >
      {percentage.format(progress)}
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          columnGap: 2.5,
          rowGap: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <SegmentedControl
          value={shown}
          onChange={setShown}
          ariaLabel={choiceLabel}
          options={[
            { value: "primary", label: primaryLabel },
            { value: "secondary", label: secondaryLabel },
          ]}
        />
      </Box>
      <Box
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: theme.spacing(4.25),
          mt: 1.25,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: trackColor ?? theme.palette.action.hover,
          color: "text.secondary",
        }}
      >
        <Box
          sx={{
            flex: "none",
            width: `${clamp(progress, 0, 1) * 100}%`,
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: fill,
            color: barInk ?? theme.palette.getContrastText(fill),
          }}
        >
          {isFigureOnBar && figure}
        </Box>
        {!isFigureOnBar && figure}
      </Box>
    </Box>
  );
};
